import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { analyticsEvents } from '../../../services/firebase/analytics-events.service';
import { firebaseAnalytics } from '../../../services/firebase/firebase.service';
import {
	InstallMethod,
	isStandaloneDisplay,
	markInstalled,
	markNeverShowAgain,
	markNudgeShown,
	offersNeverShowAgain,
	promptNativeInstall,
	readNudgeState,
	shouldShowInstallNudge,
	subscribeToInstallChanges,
	wasInstalledThisSession,
	writeNudgeState,
} from '../../../services/pwa';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { optionalEnv } from '../../../utils/env';
import { navigationRef } from '../../../utils/helpers/navigationService';
import { useInstallMethod } from '../../../utils/hooks/useInstallPrompt';
import InstallAppModal from './InstallAppModal';

/**
 * Decides when the "install Svara as an app" nudge appears and records what the player
 * did with it. Mounted once, by the web app root; the native apps never see it.
 *
 * It waits for a logged-in player sitting on the home screen, then gives the screen a
 * moment to settle before asking - and asks at most once per page load, whatever the
 * schedule says, so a player bouncing between screens is not asked twice. The schedule
 * itself (iOS once, Android every two weeks) is installNudgePolicy's.
 *
 * `PWA_INSTALL_NUDGE=false` in the environment turns the nudge off without touching the
 * manifest or the service worker, for when a release has to ship with the install flow
 * not yet verified on devices.
 */
const NUDGE_DELAY_MS = 4000;
const HOME_ROUTE = 'Main';

type OpenNudge = { method: Exclude<InstallMethod, 'none'>; offerNeverShowAgain: boolean };

const isNudgeEnabled = () => optionalEnv('PWA_INSTALL_NUDGE')?.toLowerCase() !== 'false';

/**
 * Development builds only: `?pwaNudge=android` or `?pwaNudge=ios` opens that variant of the
 * nudge at once - no login, no platform check, no schedule, nothing recorded - so the screens
 * can be looked at on a phone against a dev server. Compiled out of production builds.
 */
const devForcedMethod = (): Exclude<InstallMethod, 'none'> | null => {
	if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return null;
	const value = new URLSearchParams(window.location.search).get('pwaNudge');
	if (value === 'android') return 'native';
	if (value === 'ios') return 'manual-ios';
	return null;
};

const useIsOnHomeScreen = () => {
	const [onHome, setOnHome] = useState(false);

	useEffect(() => {
		const update = () => {
			setOnHome(navigationRef.isReady() && navigationRef.getCurrentRoute()?.name === HOME_ROUTE);
		};
		update();
		return navigationRef.addListener('state', update);
	}, []);

	return onHome;
};

export const InstallNudgeHost = () => {
	const isLogged = useSelector(selectIsLogged);
	const onHome = useIsOnHomeScreen();
	const method = useInstallMethod();
	const [open, setOpen] = useState<OpenNudge | null>(null);
	const askedThisLoadRef = useRef(false);
	const installReportedRef = useRef(false);

	// Lets analytics split installed-app sessions from browser ones.
	useEffect(() => {
		firebaseAnalytics
			.setUserProperty('display_mode', isStandaloneDisplay() ? 'standalone' : 'browser')
			.catch(() => {});
	}, []);

	// The browser's own word that the app went on the home screen - from our prompt or
	// from the browser's menu - ends the schedule for good.
	useEffect(
		() =>
			subscribeToInstallChanges(() => {
				if (!wasInstalledThisSession() || installReportedRef.current) return;
				installReportedRef.current = true;
				writeNudgeState(markInstalled(readNudgeState()));
				analyticsEvents.trackPwaInstallNudge('installed', 'native');
				setOpen(null);
			}),
		[],
	);

	useEffect(() => {
		if (open || askedThisLoadRef.current) return;

		const forced = devForcedMethod();
		if (forced) {
			askedThisLoadRef.current = true;
			setOpen({ method: forced, offerNeverShowAgain: true });
			return;
		}

		if (!isNudgeEnabled() || !isLogged || !onHome || method === 'none') return;

		const state = readNudgeState();
		if (!shouldShowInstallNudge(method, state)) return;

		const timer = setTimeout(() => {
			askedThisLoadRef.current = true;
			writeNudgeState(markNudgeShown(state));
			analyticsEvents.trackPwaInstallNudge('shown', method);
			setOpen({ method, offerNeverShowAgain: offersNeverShowAgain(method, state) });
		}, NUDGE_DELAY_MS);

		return () => clearTimeout(timer);
	}, [open, isLogged, onHome, method]);

	const handleInstall = useCallback(async () => {
		if (!open) return;
		analyticsEvents.trackPwaInstallNudge('install', open.method);
		setOpen(null);

		const outcome = await promptNativeInstall();
		if (outcome === 'unavailable') return;
		analyticsEvents.trackPwaInstallNudge(outcome, open.method);
		if (outcome === 'accepted') {
			writeNudgeState(markInstalled(readNudgeState()));
		}
	}, [open]);

	const handleDismiss = useCallback(
		(neverShowAgain: boolean) => {
			if (!open) return;
			if (neverShowAgain) {
				writeNudgeState(markNeverShowAgain(readNudgeState()));
			}
			analyticsEvents.trackPwaInstallNudge(neverShowAgain ? 'never_show' : 'not_now', open.method);
			setOpen(null);
		},
		[open],
	);

	if (!open) return null;

	return (
		<InstallAppModal
			method={open.method}
			offerNeverShowAgain={open.offerNeverShowAgain}
			onInstall={handleInstall}
			onDismiss={handleDismiss}
		/>
	);
};

export default InstallNudgeHost;
